// Domain 06 check helpers — read live (cloned) state; never mutate the lab.
import { clone } from '../../engine/net/model.js';
import { roundTrip } from '../../engine/net/sim.js';
import { remoteConnect, remoteLogin } from '../../engine/net/mgmtAccess.js';
import { mgmtOf, evalAcl, aclEntries, aclKind, authenticate, pwMatches } from '../../engine/net/security.js';
import { aaaDeps } from '../../engine/net/mgmtAccess.js';
import { CRED, HOSTS } from './topologies.js';
export * from '../d05/helpers.js';

export const M = (rt, id) => mgmtOf(rt.net.devices[id]);
/** Would a TCP connection to a management service open (reachability + VTY checks)? */
export const connects = (rt, from, ip, proto = 'ssh') => remoteConnect(clone(rt.net), from, ip, proto).stage === 'ok';
/** Full remote login on a copy of the lab: connect + authenticate. */
export function canLogin(rt, from, ip, proto, user, pass) {
  const n = clone(rt.net);
  const c = remoteConnect(n, from, ip, proto);
  if (c.stage !== 'ok') return false;
  if (c.noPassword) return false;
  return remoteLogin(n, c.id, { user, pass, src: c.src, proto }).ok;
}
/** Privilege a successful login receives (0 when it fails). */
export function loginPriv(rt, from, ip, proto, user, pass) {
  const n = clone(rt.net);
  const c = remoteConnect(n, from, ip, proto);
  if (c.stage !== 'ok') return 0;
  const r = remoteLogin(n, c.id, { user, pass, src: c.src, proto });
  return r.ok ? r.priv : 0;
}
/** Console login result on a copy (line password / local / AAA). */
export const consoleLogin = (rt, id, user, pass) => authenticate(clone(rt.net), id, { line: 'con', user, pass, src: 'console' }, aaaDeps).ok;
export const tcpOk = (rt, src, dst, port) => roundTrip(clone(rt.net), src, dst, { proto: 'tcp', port }).ok;
export const pingOk = (rt, src, dst) => roundTrip(clone(rt.net), src, dst).ok;
/** What an ACL decides for a packet (no counters touched). */
export const aclSays = (rt, id, name, pkt) => evalAcl(clone(rt.net).devices[id], name, pkt).permit;
export const aclOn = (rt, id, ifc, dir) => rt.net.devices[id].interfaces[ifc]?.accessGroup?.[dir] || null;
export { aclEntries, aclKind, CRED, HOSTS, pwMatches };

// ------------------------------------------------------------ evidence
/** A successful (or failed) SSH / Telnet login from a Windows host. */
export const remoteLogged = (device, proto, { success = true, user = null, target = null } = {}) => (ev) =>
  ev.type === 'cmd' && ev.device === device && ev.key === proto && !!ev.result?.success === success && (success || ['auth', 'refused', 'reset', 'timeout', 'no-password'].includes(ev.result?.stage))
  && (!user || ev.args?.user === user) && (!target || ev.args?.ip === target);
/** A connection attempt that reached the login prompt (port open, VTY accepted the source). */
export const remoteReached = (device, proto, target) => (ev) =>
  ev.type === 'cmd' && ev.device === device && (ev.key === `${proto} connect` || (ev.key === proto && (ev.result?.success || ev.result?.stage === 'auth' || ev.result?.stage === 'no-password'))) && (!target || ev.args?.ip === target);
/** Enable mode reached by answering the password prompt correctly. */
export const enabledWithSecret = (device) => (ev) => ev.type === 'cmd' && ev.device === device && ev.key === 'enable' && ev.promptBefore === 'Password: ' && ev.ok !== false;
/** Console login completed after logging out. */
export const consoleLoggedIn = (device) => (ev) => ev.type === 'cmd' && ev.device === device && ev.key === 'console login' && ev.ok !== false;

// ------------------------------------------------------------ design checks shared by several missions
export const secretOk = (rt, id) => { const m = M(rt, id); return !!m.enableSecret && pwMatches(m.enableSecret, CRED.enable) && !m.enablePassword && m.encrypt && m.minLength >= 10; };
export const consoleOk = (rt, id) => { const L = M(rt, id).lines.con; return L.password === CRED.console && L.login === 'line' && !!L.execTimeout && (L.execTimeout[0] > 0 || L.execTimeout[1] > 0) && L.execTimeout[0] <= 15; };
export const bannerOk = (rt, id) => /authori[sz]ed/i.test(M(rt, id).banner || '');
export function sshOnly(rt, id) {
  const m = M(rt, id);
  return !!m.rsa && m.rsa.bits >= 2048 && m.ssh.version === 2 && m.lines.vty.transport.length === 1 && m.lines.vty.transport[0] === 'ssh';
}
export const adminUser = (rt, id) => { const u = M(rt, id).users[CRED.admin.user]; return !!u && u.secret && u.priv === 15 && pwMatches(u, CRED.admin.pass); };
/** VTY access-class allows only the management subnet. */
export function vtyRestricted(rt, id) {
  const d = rt.net.devices[id];
  const ac = mgmtOf(d).lines.vty.accessClass;
  if (!ac || !aclEntries(d, ac)) return false;
  const ok = (src) => evalAcl(clone(d), ac, { src, dst: '10.60.99.1', proto: 'tcp', dport: 22 }).permit;
  return ok(HOSTS['ADMIN-PC'].ip) && ok('10.60.99.20') && !ok(HOSTS['PC-01'].ip) && !ok(HOSTS.OUTSIDE.ip) && !ok(HOSTS.CONTRACTOR.ip);
}
const MGMT_IP = { R1: '10.60.99.1', SW1: '10.60.10.2' };
export const adminSsh = (rt, id) => canLogin(rt, 'ADMIN-PC', MGMT_IP[id], 'ssh', CRED.admin.user, CRED.admin.pass);
export { MGMT_IP };
